import React from 'react';
import { Form, Input, Select, DatePicker, Checkbox, Button, AutoComplete } from 'antd';
import type { FormRule } from 'antd';
import StateSelect from 'components/form/StateSelect';
import FileUpload from 'components/form/FileUploadPrivate';
import SectionHeader from '../../components/SectionHeader';
import OptLabel from '../../components/OptLabel';
import OptionalSection from '../../components/OptionalSection';
import InfoTip from '../../components/InfoTip';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { CERT_BODY, COMPACTS, selectOptions } from '../../constants';
import s from '../../s.module.less';

// 选填条目是否真的填了内容：表单默认塞一条空行 [{}]，id / licenseType 不算用户填写的内容
const hasEntryValue = (o: any) =>
    !!o &&
    Object.entries(o).some(
        ([k, val]) =>
            k !== 'id' && k !== 'licenseType' && val != null && val !== '' && !(Array.isArray(val) && !val.length),
    );

// 兼容三种选填字段形态：条目数组（deaLicenses）、单对象（cdsCertificate）、字符串数组（multistateAuthorities）
const hasSectionValue = (v: any): boolean => {
    if (Array.isArray(v)) return v.some((item) => (typeof item === 'string' ? !!item : hasEntryValue(item)));
    return hasEntryValue(v);
};

// 选填区块「有内容就默认展开」的包装。
// 必须用 Form.Item shouldUpdate 的 getFieldValue 读 store，不能用 Form.useWatch：
// 区块折叠时里面的 Form.Item 还没挂载，而 rc-field-form 的 notifyWatch 只回传「已注册字段」的值
// （useWatch 里 getFieldsValue() 不带 true），所以 useWatch 永远拿不到接口回填的 deaLicenses —— 永远不会自动展开。
const AutoOpenSection = ({ name, title, children }: { name: string; title: string; children: React.ReactNode }) => (
    <Form.Item noStyle shouldUpdate={(prev, cur) => prev[name] !== cur[name]}>
        {({ getFieldValue }) => (
            <OptionalSection title={title} hasValue={hasSectionValue(getFieldValue(name))}>
                {children}
            </OptionalSection>
        )}
    </Form.Item>
);

// Board certification 上传了证书文件时，认证机构 / 证书号 / 到期日必填。拦截口径在 reviewSpec（只在 Review 校验），
// 这里的 rule 只为让 Review 用 setFields 标的红在输入后能被清掉；不加 dependencies，避免向导里一上传文件就立刻标红
const boardRequiredWhenDoc =
    (fieldName: number): FormRule =>
    ({ getFieldValue }) => ({
        validator: (_, val) =>
            getFieldValue(['boardCertifications', fieldName, 'url']) && (val == null || val === '')
                ? Promise.reject(new Error('Required'))
                : Promise.resolve(),
    });

export default function StepLicenses() {
    // ID type 选项来自 setting type=IDENTITY_TYPE（与主 UniProfile 的 PersonalIdentification 一致），
    // 存 dict_key（driver-license / passport / identity-card），不再用本地硬编码 label。
    const [idTypeOptions] = useGetSettingData(EnumFields.IDENTITY_TYPE);
    return (
        <>
            <div className={s.wizSection}>
                <SectionHeader title="Practice licenses" />
                {/* 执业许可类型（LCSW/APRN/MD…）存后端 LicenseDto.type；licenseType 保持 null */}
                <Form.List name="practiceLicenses" initialValue={[{}]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, idx) => (
                                <div
                                    key={field.key}
                                    className={idx > 0 ? `${s.wizEntry} ${s.entryDivider}` : s.wizEntry}
                                >
                                    <div className={s.entryRow}>
                                        <div className={s.entryTitle}>License {idx + 1}</div>
                                        {/* 每条执业许可都能删，包括只剩一条时的第一条 */}
                                        <button
                                            type="button"
                                            className={s.refRemove}
                                            onClick={() => remove(field.name)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                    <div className={s.wizRow}>
                                        <Form.Item
                                            name={[field.name, 'state']}
                                            label="State for licenses"
                                            rules={[{ required: true, message: 'Required' }]}
                                        >
                                            <StateSelect size="large" placeholder="Select state" />
                                        </Form.Item>
                                        <Form.Item
                                            name={[field.name, 'type']}
                                            label="License type"
                                            rules={[{ required: true, message: 'Required' }]}
                                        >
                                            <Input size="large" placeholder="e.g. LCSW, APRN, MD" />
                                        </Form.Item>
                                    </div>
                                    <Form.Item
                                        name={[field.name, 'licenseNumber']}
                                        label="License number"
                                        rules={[{ required: true, message: 'Required' }]}
                                    >
                                        <Input size="large" placeholder="Enter your license number" />
                                    </Form.Item>
                                    <div className={s.wizRow}>
                                        <Form.Item
                                            name={[field.name, 'effectiveDate']}
                                            label={<OptLabel>Issue date</OptLabel>}
                                        >
                                            <DatePicker size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                        <Form.Item
                                            name={[field.name, 'expireDate']}
                                            label="Expiration date"
                                            rules={[{ required: true, message: 'Required' }]}
                                        >
                                            <DatePicker size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <p className={s.uploadLabel}>Upload the copy of the license</p>
                                        <Form.Item
                                            name={[field.name, 'url']}
                                            rules={[{ required: true, message: 'Upload a copy of the license' }]}
                                        >
                                            <FileUpload uploadType="LICENSE" accept=".pdf,.jpg,.jpeg,.png" />
                                        </Form.Item>
                                    </div>
                                </div>
                            ))}
                            <Button block disabled={fields.length >= 51} onClick={() => add({})} className={s.refAdd}>
                                + Add another medical license
                            </Button>
                        </>
                    )}
                </Form.List>
            </div>

            <div className={s.wizSection}>
                <SectionHeader title="Personal identification" />
                <Form.Item
                    name={['identification', 'type']}
                    label="ID type"
                    initialValue="driver-license"
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <Select
                        size="large"
                        placeholder="Select"
                        fieldNames={{ label: 'content', value: 'dictKey' }}
                        options={idTypeOptions as any}
                    />
                </Form.Item>
                <div>
                    <p className={s.uploadLabel}>Upload the government-issued ID</p>
                    <div className={s.wizRow}>
                        <Form.Item
                            name={['identification', 'frontUrl']}
                            label="Front"
                            rules={[{ required: true, message: 'Upload the front of your ID' }]}
                        >
                            <FileUpload uploadType="LICENSE" accept=".pdf,.jpg,.jpeg,.png" />
                        </Form.Item>
                        <Form.Item name={['identification', 'backUrl']} label={<OptLabel>Back</OptLabel>}>
                            <FileUpload uploadType="LICENSE" accept=".pdf,.jpg,.jpeg,.png" />
                        </Form.Item>
                    </div>
                </div>
            </div>

            <div className={s.wizSection}>
                <div className={`${s.wizSectionHeader} ${s.wizSectionHeaderStack}`}>
                    <span className={s.optTitle}>Board certification</span>
                    <p className={s.hint}>Board certification is required for prescribers (MDs/DOs, NPs, PAs)</p>
                </div>
                <Form.List name="boardCertifications" initialValue={[{}]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, idx) => (
                                <div
                                    key={field.key}
                                    className={idx > 0 ? `${s.wizEntry} ${s.entryDivider}` : s.wizEntry}
                                >
                                    <div className={s.entryRow}>
                                        <div className={s.entryTitle}>Board certification {idx + 1}</div>
                                        {idx > 0 && (
                                            <button
                                                type="button"
                                                className={s.refRemove}
                                                onClick={() => remove(field.name)}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <div className={s.wizRow}>
                                        <Form.Item
                                            name={[field.name, 'boardBody']}
                                            label="Certifying body"
                                            rules={[boardRequiredWhenDoc(field.name)]}
                                        >
                                            <AutoComplete
                                                size="large"
                                                placeholder="Select or enter"
                                                options={CERT_BODY.filter((c) => c !== 'Other').map((c) => ({
                                                    value: c,
                                                }))}
                                                filterOption={(input, option) =>
                                                    (option?.value as string)
                                                        .toLowerCase()
                                                        .includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name={[field.name, 'expireDate']}
                                            label="Certification expiry"
                                            rules={[boardRequiredWhenDoc(field.name)]}
                                        >
                                            <DatePicker size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </div>
                                    {/* 证书号存 LicenseDto.licenseNumber（Carelon 等 payer 需要；历史数据由运营从证书文件补），上传了证书文件时在 Review 必填 */}
                                    <Form.Item
                                        name={[field.name, 'licenseNumber']}
                                        label="Certificate number"
                                        rules={[boardRequiredWhenDoc(field.name)]}
                                    >
                                        <Input size="large" placeholder="Enter your certificate number" />
                                    </Form.Item>
                                    <div>
                                        <p className={s.uploadLabel}>
                                            Certification document
                                            <InfoTip title="Your ANCC or AANP certification document showing your credential (PMHNP-BC, FNP-BC, etc.) and expiration date." />
                                        </p>
                                        <Form.Item name={[field.name, 'url']}>
                                            <FileUpload uploadType="LICENSE" accept=".pdf,.jpg,.jpeg,.png" />
                                        </Form.Item>
                                    </div>
                                </div>
                            ))}
                            <Button block disabled={fields.length >= 5} onClick={() => add()} className={s.refAdd}>
                                + Add another board certification
                            </Button>
                        </>
                    )}
                </Form.List>
            </div>

            <AutoOpenSection name="deaLicenses" title="DEA licenses">
                <Form.List name="deaLicenses" initialValue={[{}]}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, idx) => (
                                <div
                                    key={field.key}
                                    className={idx > 0 ? `${s.wizEntry} ${s.entryDivider}` : s.wizEntry}
                                >
                                    <div className={s.entryRow}>
                                        <div className={s.entryTitle}>DEA license {idx + 1}</div>
                                        {idx > 0 && (
                                            <button
                                                type="button"
                                                className={s.refRemove}
                                                onClick={() => remove(field.name)}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                    <Form.Item name={[field.name, 'state']} label="License state">
                                        <StateSelect size="large" placeholder="Select state" />
                                    </Form.Item>
                                    <div className={s.wizRow}>
                                        <Form.Item name={[field.name, 'licenseNumber']} label="DEA license number">
                                            <Input size="large" placeholder="What is the DEA license number?" />
                                        </Form.Item>
                                        <Form.Item name={[field.name, 'expireDate']} label="Expiration date">
                                            <DatePicker size="large" style={{ width: '100%' }} />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <p className={s.uploadLabel}>License picture</p>
                                        <Form.Item name={[field.name, 'url']} noStyle>
                                            <FileUpload uploadType="LICENSE" accept=".pdf,.jpg,.jpeg,.png" />
                                        </Form.Item>
                                    </div>
                                </div>
                            ))}
                            <Button block onClick={() => add()} className={s.refAdd}>
                                + Add another DEA license
                            </Button>
                        </>
                    )}
                </Form.List>
            </AutoOpenSection>

            <AutoOpenSection name="cdsCertificate" title="State controlled substance (CDS) certificate">
                <div className={s.wizRow}>
                    <Form.Item name={['cdsCertificate', 'state']} label="CDS state">
                        <StateSelect size="large" placeholder="Select state" />
                    </Form.Item>
                    <Form.Item
                        name={['cdsCertificate', 'licenseNumber']}
                        label={<OptLabel>CDS certificate number</OptLabel>}
                    >
                        <Input size="large" placeholder="Certificate number" />
                    </Form.Item>
                </div>
                <Form.Item name={['cdsCertificate', 'expireDate']} label={<OptLabel>CDS expiration date</OptLabel>}>
                    <DatePicker size="large" style={{ width: '100%' }} />
                </Form.Item>
            </AutoOpenSection>

            <AutoOpenSection name="multistateAuthorities" title="Multistate authority">
                <p className={s.hint}>Select the compact(s) you belong to.</p>
                <Form.Item name="multistateAuthorities">
                    <Checkbox.Group className={s.compactGroup} options={selectOptions(COMPACTS)} />
                </Form.Item>
            </AutoOpenSection>
        </>
    );
}
